import { datadogRum } from "@datadog/browser-rum";
import { useContext, useEffect } from "react";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

/**
 * Reports the user's opt-in decisions to Datadog RUM as feature flag evaluations, which tag the
 * session and every view in it, and as global context, which tags every event.
 *
 * Deliberately separate from `AccountFlags`, which reports the ~245 builder flags through the same
 * SDK method. Those are one value per builder set in BTAdmin; these are per person and follow the
 * user across builders and devices — different scope, source, and lifetime, so they do not share a
 * reporter. Add future opt-ins here rather than starting a parallel component.
 *
 * This is what lets Datadog decide what to keep. Under RUM Without Limits a retention filter
 * selects sessions by query — e.g. `@feature_flags.optins_infoArch:true` at a 100% replay sample
 * rate — so changing who gets recorded is a config edit in Datadog rather than a frontend deploy.
 * That is the reason this reports state instead of calling `startSessionReplayRecording({ force:
 * true })`: forcing hard-codes the cohort into the bundle, and the resulting
 * `@session.has_forced_replay` tag cannot distinguish one forcing caller from another.
 *
 * Evaluations are emitted for opted-out users too, not just opted-in. The filter only needs
 * `true`, but reporting both makes the two groups comparable in the RUM Explorer, and a flag that
 * is absent is indistinguishable from one that was never evaluated.
 *
 * Mounted once in `AppProvider` so this runs on every session, not only the one where the user
 * flipped the toggle. A filter keyed on a flag that is emitted only at toggle time would silently
 * capture a sliver of the cohort while appearing to work.
 *
 * Renders nothing — it exists to run an effect, the same way its sibling {@link UserTrace} does.
 */
export const OptInFeatureFlags = () => {
    const userInfo = useContext(UserInfoContext);

    // `optIns` is absent until the global-info payload resolves. Distinguishing that from a real
    // decision matters: reporting `false` too early would tag the session as opted-out for a user
    // who is opted in, and the flag context persists for the rest of the session.
    const infoArchOptIn = userInfo?.optIns?.infoArch;
    const hasDecision = infoArchOptIn !== undefined;

    useEffect(() => {
        if (!hasDecision) {
            return;
        }

        // Ask the SDK whether init ran rather than re-deriving "enabled" from a second config
        // source — RUM is gated per-environment by a DB AppDefault and may never have initialized,
        // in which case every datadogRum call is a silent no-op.
        if (!datadogRum.getInitConfiguration()) {
            return;
        }

        // Namespaced so later opt-ins can join without colliding with unrelated flags. The
        // separator is an underscore, not a dot: Datadog does not support `.` (among other
        // characters) in feature flag keys and its own docs recommend substituting before
        // sending. Every existing key here is plain camelCase for the same reason.
        // The tri-state collapses to a boolean here, the same way the UI does: only an explicit
        // `true` is opted in, and "never decided" reads as `false`. The two are still separable
        // through the `infoArchOptIn` RUM action, which carries the decision itself.
        const isOptedIn = infoArchOptIn === true;
        datadogRum.addFeatureFlagEvaluation("optins_infoArch", isOptedIn);

        // The flag above only lands on view and error events. Global context lands on every event
        // type — actions and resources included — which is what lets the Bills baseline compare
        // Research Preview against the current UI on any metric. Lands as `@context.researchPreview`.
        datadogRum.setGlobalContextProperty("researchPreview", isOptedIn);
    }, [hasDecision, infoArchOptIn]);

    return null;
};
